import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CustomerDetailsPanel = ({ customer, appointments = [] }) => {
  if (!customer) {
    return (
      <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-6 md:p-8">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4 md:w-20 md:h-20">
            <Icon name="UserX" size={32} className="text-muted-foreground md:w-10 md:h-10" />
          </div>
          <h3 className="text-lg font-heading font-semibold text-foreground mb-2 md:text-xl">
            No Customer Selected
          </h3>
          <p className="text-sm text-muted-foreground max-w-xs md:text-base">
            Select a customer from the booking form to view their details and appointment history
          </p>
        </div>
      </div>
    );
  }

  const customerAppointments = appointments?.filter(apt => apt?.customerId === customer?.id);
  const upcomingAppointments = customerAppointments?.filter(apt => new Date(apt.date) >= new Date());
  const pastAppointments = customerAppointments?.filter(apt => new Date(apt.date) < new Date());

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2">
      <div className="px-6 py-5 border-b border-border md:px-8 md:py-6">
        <h2 className="text-xl font-heading font-semibold text-foreground md:text-2xl">
          Customer Details
        </h2>
        <p className="text-sm text-muted-foreground mt-1 md:text-base">
          Contact information and appointment history
        </p>
      </div>
      <div className="p-6 space-y-6 md:p-8">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 md:w-20 md:h-20">
            {customer?.avatar ? (
              <Image
                src={customer?.avatar}
                alt={customer?.avatarAlt || `Profile photo of ${customer?.name}`}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <Icon name="User" size={32} className="text-primary md:w-10 md:h-10" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-heading font-semibold text-foreground mb-1 md:text-xl">
              {customer?.name}
            </h3>
            <p className="text-sm text-muted-foreground md:text-base">
              Customer since {new Date(customer.createdAt)?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="Mail" size={20} className="text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-caption text-muted-foreground mb-1 md:text-sm">
                Email Address
              </p>
              <p className="text-sm font-medium text-foreground break-all md:text-base">
                {customer?.email}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="Phone" size={20} className="text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-caption text-muted-foreground mb-1 md:text-sm">
                Phone Number
              </p>
              <p className="text-sm font-medium text-foreground font-data md:text-base">
                {customer?.phone}
              </p>
            </div>
          </div>
        </div>

        <div className="pt-6 border-t border-border">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-base font-heading font-semibold text-foreground md:text-lg">
              Appointment History
            </h4>
            <div className="flex items-center gap-2">
              <span className="text-xs font-caption text-muted-foreground md:text-sm">
                Total: {customerAppointments?.length}
              </span>
            </div>
          </div>

          {customerAppointments?.length === 0 ? (
            <div className="py-8 text-center">
              <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3 md:w-16 md:h-16">
                <Icon name="Calendar" size={24} className="text-muted-foreground md:w-8 md:h-8" />
              </div>
              <p className="text-sm text-muted-foreground md:text-base">
                No appointments yet
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {upcomingAppointments?.length > 0 && (
                <div>
                  <p className="text-xs font-caption font-medium text-muted-foreground mb-3 md:text-sm">
                    UPCOMING ({upcomingAppointments?.length})
                  </p>
                  <div className="space-y-2">
                    {upcomingAppointments?.slice(0, 3)?.map((apt) => (
                      <div
                        key={apt?.id}
                        className="p-3 bg-accent/5 border border-accent/20 rounded-lg md:p-4"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <p className="text-sm font-medium text-foreground md:text-base">
                            {apt?.service}
                          </p>
                          <span className="px-2 py-1 bg-accent/10 text-accent text-xs font-caption font-medium rounded whitespace-nowrap">
                            {apt?.duration} min
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground md:text-sm">
                          <div className="flex items-center gap-1">
                            <Icon name="Calendar" size={14} />
                            <span>{new Date(apt.date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Icon name="Clock" size={14} />
                            <span className="font-data">{apt?.time}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {pastAppointments?.length > 0 && (
                <div>
                  <p className="text-xs font-caption font-medium text-muted-foreground mb-3 md:text-sm">
                    PAST ({pastAppointments?.length})
                  </p>
                  <div className="space-y-2">
                    {pastAppointments?.slice(0, 2)?.map((apt) => (
                      <div
                        key={apt?.id}
                        className="p-3 bg-muted/50 border border-border rounded-lg md:p-4"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <p className="text-sm font-medium text-foreground md:text-base">
                            {apt?.service}
                          </p>
                          <span className="px-2 py-1 bg-muted text-muted-foreground text-xs font-caption font-medium rounded whitespace-nowrap">
                            {apt?.duration} min
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground md:text-sm">
                          <div className="flex items-center gap-1">
                            <Icon name="Calendar" size={14} />
                            <span>{new Date(apt.date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Icon name="Clock" size={14} />
                            <span className="font-data">{apt?.time}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDetailsPanel;